import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { useAppStore } from '@/stores/appStore';
import { useResearchStore } from '@/stores/researchStore';
import { searchExpleeCompanies } from '@/services/explee';
import { getCredits } from '@/services/credits';
import { supabase } from '@/services/supabase';
import { v4 as uuidv4 } from 'uuid';

type ExpleeCompanyRow = {
  name?: string;
  domain?: string;
  url?: string;
  description?: string;
  description_website?: string;
  industry?: string;
  industries_nace?: Array<{ sector: string; sector_score: number; description: string; code: string; activity_score: number }>;
  size?: number;
  founded?: number;
  traffic?: number;
  traffic_growth?: number;
  traffic_by_country?: Record<string, number>;
  traffic_by_months?: Record<string, number>;
  revenue_annual?: number;
  funding_stage?: string;
  funding_date?: string;
  funding_last_round_stage?: string;
  funding_last_round_date?: string;
  funding_last_round_amount?: number;
  hiring?: boolean | null;
  hiring_is?: boolean | null;
  geo?: string;
  geo_subdivision?: string;
  geo_city?: string;
  geo_countries?: Record<string, number>;
  geo_country_employees?: Record<string, number>;
  geo_countries_traffic?: Record<string, number>;
  geo_founders?: string[];
  employees_by_department?: Record<string, number>;
  web_metrics_bounce_rate?: number | null;
  web_metrics_pages_per_visit?: number | null;
  web_metrics_time_on_site?: number | null;
  marketing_pixels?: string[];
  emails?: string[];
  phones?: string[] | null;
  social_facebook_urls?: string[];
  social_instagram_urls?: string[];
  social_x_urls?: string[];
  social_tiktok_urls?: string[];
  social_youtube_urls?: string[];
  social_linkedin_urls?: string[];
  linkedin_id?: number | null;
  domain_is_alive?: number;
  criteria?: Record<string, { reasoning: string; score: number }>;
  match?: number;
};

function fmt(n: number | null | undefined, prefix = '') {
  if (n == null) return '-';
  if (n >= 1_000_000) return `${prefix}${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${prefix}${(n / 1_000).toFixed(1)}K`;
  return `${prefix}${n}`;
}

function ScoreBadge({ score }: { score: number }) {
  const colors: Record<number, string> = {
    5: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    4: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
    3: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
    2: 'bg-orange-500/20 text-orange-400 border border-orange-500/30',
    1: 'bg-red-500/20 text-red-400 border border-red-500/30',
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold ${colors[score] || colors[1]}`}>
      {score}
    </span>
  );
}



function CollapsibleSection({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-800/60 hover:bg-gray-800 text-left"
      >
        <span className="text-xs font-semibold text-gray-300 uppercase tracking-wider">{title}</span>
        <span className="text-gray-500 text-sm">{open ? '▲' : '▼'}</span>
      </button>
      {open && <div className="px-4 py-3 bg-gray-900/40">{children}</div>}
    </div>
  );
}

function KVGrid({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (!entries.length) return <p className="text-xs text-gray-500">No data</p>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {entries.map(([k, v]) => (
        <div key={k} className="bg-gray-800/50 rounded px-2 py-1.5">
          <div className="text-[10px] text-gray-500 uppercase tracking-wide">{k.replace(/_/g, ' ')}</div>
          <div className="text-xs text-gray-200 truncate">{String(v)}</div>
        </div>
      ))}
    </div>
  );
}

function DescriptionCell({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div onClick={() => setExpanded(!expanded)} className="cursor-pointer">
      <div className={expanded ? '' : 'line-clamp-2'}>{text}</div>
      {!expanded && text.length > 100 && <span className="text-blue-400 text-[10px]">more</span>}
    </div>
  );
}

function ExpandedRow({ c, maxMatch }: { c: ExpleeCompanyRow; maxMatch: number }) {
  const deptData = Object.fromEntries(
    Object.entries(c.employees_by_department || {}).map(([k, v]) => [k.replace('employees_count_', ''), v])
  );

  return (
    <div className="px-4 pb-4 grid grid-cols-1 lg:grid-cols-2 gap-3">

      {/* Criteria Scores */}
      {c.criteria && Object.keys(c.criteria).length > 0 && (
        <div className="lg:col-span-2">
          <CollapsibleSection title={`Criteria Scores · Match ${c.match ?? 0}/${maxMatch}`}>
            <div className="space-y-2">
              {Object.entries(c.criteria).map(([key, val]) => (
                <div key={key} className="flex items-start gap-3 bg-gray-800/40 rounded p-2">
                  <ScoreBadge score={val.score} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-200">{key}</div>
                    <div className="text-xs text-gray-400 mt-0.5 italic">{val.reasoning}</div>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        </div>
      )}

      {/* Traffic & Web Metrics */}
      <CollapsibleSection title="Traffic & Web Metrics">
        <div className="space-y-2">
          <KVGrid data={{
            'Monthly Traffic': fmt(c.traffic),
            'Traffic Growth %': c.traffic_growth != null ? `${c.traffic_growth}%` : '-',
            'Bounce Rate': c.web_metrics_bounce_rate != null ? `${(c.web_metrics_bounce_rate * 100).toFixed(0)}%` : '-',
            'Pages / Visit': c.web_metrics_pages_per_visit ?? '-',
            'Time on Site (s)': c.web_metrics_time_on_site ?? '-',
            'Marketing Pixels': c.marketing_pixels?.join(', ') || '-',
          }} />
          {c.traffic_by_country && Object.keys(c.traffic_by_country).length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Traffic by Country</div>
              <div className="flex flex-wrap gap-1">
                {Object.entries(c.traffic_by_country).slice(0, 8).map(([country, visits]) => (
                  <span key={country} className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded">
                    {country}: {fmt(visits)}
                  </span>
                ))}
              </div>
            </div>
          )}
          {c.traffic_by_months && Object.keys(c.traffic_by_months).length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Monthly Traffic History</div>
              <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                {Object.entries(c.traffic_by_months).slice(-12).map(([month, visits]) => (
                  <span key={month} className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded">
                    {month.slice(0, 7)}: {fmt(visits)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Geo & Employees */}
      <CollapsibleSection title="Geo & Employees">
        <div className="space-y-2">
          <KVGrid data={{
            'HQ Country': c.geo || '-',
            'Region': c.geo_subdivision || '-',
            'City': c.geo_city || '-',
            'Founders From': c.geo_founders?.join(', ') || '-',
          }} />
          {c.geo_countries && Object.keys(c.geo_countries).length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Countries</div>
              <div className="flex flex-wrap gap-1">
                {Object.entries(c.geo_countries).map(([country, pct]) => (
                  <span key={country} className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded">
                    {country}: {pct}%
                  </span>
                ))}
              </div>
            </div>
          )}
          {c.geo_country_employees && Object.keys(c.geo_country_employees).length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Employees by Country</div>
              <div className="flex flex-wrap gap-1">
                {Object.entries(c.geo_country_employees).filter(([k]) => k !== 'total').map(([country, count]) => (
                  <span key={country} className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded">
                    {country}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}
          {Object.keys(deptData).length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Employees by Dept</div>
              <KVGrid data={deptData} />
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Funding & Hiring */}
      <CollapsibleSection title="Funding & Hiring">
        <KVGrid data={{
          'Annual Revenue': fmt(c.revenue_annual, '$'),
          'Funding Stage': c.funding_stage || '-',
          'Funding Date': c.funding_date || '-',
          'Last Round Stage': c.funding_last_round_stage || '-',
          'Last Round Date': c.funding_last_round_date || '-',
          'Last Round Amount': fmt(c.funding_last_round_amount, '$'),
          'Hiring': c.hiring != null ? (c.hiring ? 'Yes' : 'No') : '-',
        }} />
      </CollapsibleSection>

      {/* Contact & Social */}
      <CollapsibleSection title="Contact & Social">
        <div className="space-y-2">
          {c.emails && c.emails.length > 0 && (
            <div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Emails</div>
              <div className="flex flex-wrap gap-1">
                {c.emails.map((e) => (
                  <a key={e} href={`mailto:${e}`} className="text-xs text-blue-400 hover:underline bg-gray-800 px-2 py-0.5 rounded">{e}</a>
                ))}
              </div>
            </div>
          )}
          {c.phones && c.phones.length > 0 && (
            <div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Phones</div>
              <div className="flex flex-wrap gap-1">
                {c.phones.map((p) => (
                  <span key={p} className="text-xs text-gray-300 bg-gray-800 px-2 py-0.5 rounded">{p}</span>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-1">
            {c.social_facebook_urls?.[0] && <a href={c.social_facebook_urls[0]} target="_blank" rel="noreferrer" className="text-xs text-blue-400 hover:underline">Facebook</a>}
            {c.social_instagram_urls?.[0] && <a href={c.social_instagram_urls[0]} target="_blank" rel="noreferrer" className="text-xs text-pink-400 hover:underline">Instagram</a>}
            {c.social_x_urls?.[0] && <a href={c.social_x_urls[0]} target="_blank" rel="noreferrer" className="text-xs text-gray-300 hover:underline">X/Twitter</a>}
            {c.social_tiktok_urls?.[0] && <a href={c.social_tiktok_urls[0]} target="_blank" rel="noreferrer" className="text-xs text-purple-400 hover:underline">TikTok</a>}
            {c.social_youtube_urls?.[0] && <a href={c.social_youtube_urls[0]} target="_blank" rel="noreferrer" className="text-xs text-red-400 hover:underline">YouTube</a>}
            {c.linkedin_id && <a href={`https://linkedin.com/company/${c.linkedin_id}`} target="_blank" rel="noreferrer" className="text-xs text-blue-300 hover:underline">LinkedIn</a>}
          </div>
        </div>
      </CollapsibleSection>

    </div>
  );
}

export function CustomKpiSearch() {
  const { userEmail, setCredits } = useAppStore();
  const { resetFilters, setFilters, setPendingSearch, setPerPage } = useResearchStore();
  const navigate = useNavigate();
  const [definition, setDefinition] = useState('');
  const [criteriaInput, setCriteriaInput] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<ExpleeCompanyRow[]>([]);
  const [meta, setMeta] = useState<{ total: number; credits_charged: number; remaining_balance: number } | null>(null);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [filterGeo, setFilterGeo] = useState('');
  const [filterSizeMin, setFilterSizeMin] = useState('');
  const [filterSizeMax, setFilterSizeMax] = useState('');
  const [filterTrafficMin, setFilterTrafficMin] = useState('');
  const [filterHiring, setFilterHiring] = useState<'any' | 'true' | 'false'>('any');
  const [filterFundingStage, setFilterFundingStage] = useState('');
  
  const [sortCol, setSortCol] = useState<'match' | 'traffic' | 'size' | 'revenue_annual'>('match');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');

  const criteria = useMemo(
    () => criteriaInput.split('\n').map((c) => c.trim()).filter(Boolean),
    [criteriaInput]
  );

  const maxMatch = criteria.length;

  const handleSearch = async () => {
    if (!definition.trim()) { alert('Definition is required'); return; }
    setLoading(true);
    setError('');
    setExpandedIdx(null);
    if (page !== 1) setPage(1);
    try {
      const res = await searchExpleeCompanies({
        definition: definition.trim(),
        criteria,
        page,
        pageSize,
        ...(filterGeo.trim() && { geo: filterGeo.trim().toUpperCase().split(',').map((s: string) => s.trim()).filter(Boolean) }),
        ...(filterSizeMin && { size_min: Number(filterSizeMin) }),
        ...(filterSizeMax && { size_max: Number(filterSizeMax) }),
        ...(filterTrafficMin && { traffic_min: Number(filterTrafficMin) }),
        ...(filterHiring !== 'any' && { hiring: filterHiring === 'true' }),
        ...(filterFundingStage && { funding_stage: filterFundingStage }),
      });
      setResults(res.companies);
      setMeta({ total: res.meta.total, credits_charged: res.meta.credits_charged, remaining_balance: res.meta.remaining_balance });
      if (userEmail) getCredits(userEmail).then(setCredits).catch(() => undefined);

      // Save results to Supabase
      if (res.companies.length > 0) {
        const searchId = uuidv4();
        const rows = res.companies.map((c) => ({
          search_id: searchId,
          user_email: userEmail || null,
          definition: definition.trim(),
          criteria: criteria,
          domain: c.domain || null,
          name: c.name || null,
          url: c.url || null,
          linkedin_id: c.linkedin_id || null,
          industry: c.industry || null,
          match: c.match || null,
          criteria_scores: c.criteria || null,
          description: c.description || null,
          description_website: c.description_website || null,
          size: c.size || null,
          founded: c.founded || null,
          geo: c.geo || null,
          geo_city: c.geo_city || null,
          geo_subdivision: c.geo_subdivision || null,
          geo_countries: c.geo_countries || null,
          geo_founders: c.geo_founders || null,
          geo_country_employees: c.geo_country_employees || null,
          traffic: c.traffic || null,
          traffic_growth: c.traffic_growth || null,
          traffic_by_country: c.traffic_by_country || null,
          traffic_by_months: c.traffic_by_months || null,
          web_metrics_bounce_rate: c.web_metrics_bounce_rate || null,
          web_metrics_pages_per_visit: c.web_metrics_pages_per_visit || null,
          web_metrics_time_on_site: c.web_metrics_time_on_site || null,
          marketing_pixels: c.marketing_pixels || null,
          revenue_annual: c.revenue_annual || null,
          funding_stage: c.funding_stage || null,
          funding_last_round_stage: c.funding_last_round_stage || null,
          funding_last_round_amount: c.funding_last_round_amount || null,
          hiring: c.hiring ?? null,
          employees_by_department: c.employees_by_department || null,
          emails: c.emails || null,
          phones: c.phones || null,
          social_facebook_urls: c.social_facebook_urls || null,
          social_instagram_urls: c.social_instagram_urls || null,
          social_x_urls: c.social_x_urls || null,
          social_tiktok_urls: c.social_tiktok_urls || null,
          social_youtube_urls: c.social_youtube_urls || null,
        }));
        supabase.from('custom_kpi_results').insert(rows).then(({ error }) => {
          if (error) console.error('Supabase save error:', error);
        });
      }
    } catch (e) {
      setError((e as Error).message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleViewContacts = (company: ExpleeCompanyRow) => {
    const companyName = company.name || '';
    const domain = company.domain || '';
    resetFilters();
    setFilters({ names: [], companies: [companyName || domain].filter(Boolean), titles: [], seniorities: ['Director', 'VP', 'C-Level'], departments: [], industries: [], locations: [], employeeSizes: [], estimatedRevenues: [], keywords: [] });
    setPerPage(25);
    setPendingSearch({ filters: { names: [], companies: [companyName || domain].filter(Boolean), titles: [], seniorities: ['Director', 'VP', 'C-Level'], departments: [], industries: [], locations: [], employeeSizes: [], estimatedRevenues: [], keywords: [] }, page: 0, perPage: 25 });
    navigate('/lead-search');
  };

  const toggleSort = (col: typeof sortCol) => {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const sorted = useMemo(() => {
    return [...results].sort((a, b) => {
      const av = (a[sortCol] as number) ?? -1;
      const bv = (b[sortCol] as number) ?? -1;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  }, [results, sortCol, sortDir]);

  const SortTh = ({ col, label, center = true }: { col: typeof sortCol; label: string; center?: boolean }) => (
    <th
      onClick={() => toggleSort(col)}
      className={`px-3 py-3 text-xs font-semibold text-gray-400 uppercase bg-card-dark cursor-pointer hover:text-white select-none ${center ? 'text-center' : 'text-left'}`}
    >
      {label} {sortCol === col ? (sortDir === 'desc' ? '↓' : '↑') : ''}
    </th>
  );

  const handleExport = () => {
    if (!sorted.length) return;
    const headers = ['Name', 'Domain', 'Industry', 'Match', 'Employees', 'Traffic', 'Traffic Growth %', 'Hiring', 'Geo', 'Funding Stage', 'Description'];
    const rows = sorted.map(c => [
      c.name || '',
      c.domain || '',
      c.industry || '',
      c.match === 3 ? 'Strong' : c.match === 2 ? 'Weak' : 'None',
      c.size ?? '',
      c.traffic ?? '',
      c.traffic_growth != null ? (c.traffic_growth * 100).toFixed(0) + '%' : '',
      c.hiring != null ? (c.hiring ? 'Yes' : 'No') : '',
      [c.geo_city, c.geo].filter(Boolean).join(', '),
      c.funding_stage || '',
      (c.description || '').replace(/,/g, ' '),
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kpi-results-p${page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePage = async (newPage: number) => {
    setPage(newPage);
    setLoading(true);
    setError('');
    setExpandedIdx(null);
    try {
      const res = await searchExpleeCompanies({
        definition: definition.trim(),
        criteria,
        page: newPage,
        pageSize,
        ...(filterGeo.trim() && { geo: filterGeo.trim().toUpperCase().split(',').map((s: string) => s.trim()).filter(Boolean) }),
        ...(filterSizeMin && { size_min: Number(filterSizeMin) }),
        ...(filterSizeMax && { size_max: Number(filterSizeMax) }),
        ...(filterTrafficMin && { traffic_min: Number(filterTrafficMin) }),
        ...(filterHiring !== 'any' && { hiring: filterHiring === 'true' }),
        ...(filterFundingStage && { funding_stage: filterFundingStage }),
      });
      setResults(res.companies);
      setMeta({ total: res.meta.total, credits_charged: res.meta.credits_charged, remaining_balance: res.meta.remaining_balance });
    } catch (e) {
      setError((e as Error).message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />
      <div className="px-6 py-6">
        {/* Search Form */}
        <div className="bg-card-dark border border-gray-700 rounded-xl p-6">
          <div className="text-lg font-semibold text-white mb-4">Custom KPI Search</div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <label className="text-xs text-gray-400">Definition</label>
              <input
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                placeholder="e.g., white label GoHighLevel agency"
                value={definition}
                onChange={(e) => setDefinition(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-400">Page Size</label>
              <select
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {[25, 50, 100, 250, 500, 1000].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="lg:col-span-3">
              <label className="text-xs text-gray-400">Criteria (one per line, max 3)</label>
              <textarea
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white min-h-[96px]"
                placeholder="gohighlevel agency&#10;white label gohighlevel&#10;white label marketing agency"
                value={criteriaInput}
                onChange={(e) => setCriteriaInput(e.target.value)}
              />
              {criteria.length > 3 && (
                <p className="text-xs text-red-400 mt-1">Maximum 3 criteria allowed</p>
              )}
            </div>
          </div>
          {/* Filters Row */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="text-xs text-gray-400">Country (ISO)</label>
              <input
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                placeholder="US, GB, CA"
                value={filterGeo}
                onChange={(e) => setFilterGeo(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-400">Employees Min</label>
              <input
                type="number"
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                placeholder="10"
                value={filterSizeMin}
                onChange={(e) => setFilterSizeMin(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-400">Employees Max</label>
              <input
                type="number"
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                placeholder="500"
                value={filterSizeMax}
                onChange={(e) => setFilterSizeMax(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-400">Traffic Min</label>
              <input
                type="number"
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                placeholder="1000"
                value={filterTrafficMin}
                onChange={(e) => setFilterTrafficMin(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-400">Hiring</label>
              <select
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                value={filterHiring}
                onChange={(e) => setFilterHiring(e.target.value as 'any' | 'true' | 'false')}
              >
                <option value="any">Any</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400">Funding Stage</label>
              <select
                className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm"
                value={filterFundingStage}
                onChange={(e) => setFilterFundingStage(e.target.value)}
              >
                <option value="">Any</option>
                <option value="pre_seed">Pre-Seed</option>
                <option value="seed">Seed</option>
                <option value="series_a">Series A</option>
                <option value="series_b">Series B</option>
                <option value="series_c">Series C</option>
                <option value="series_d">Series D+</option>
                <option value="bootstrapped">Bootstrapped</option>
                <option value="ipo">IPO</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={handleSearch}
              disabled={loading || criteria.length > 3}
              className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            >
              {loading ? 'Searching…' : 'Search'}
            </button>
            {meta && (
              <div className="text-xs text-gray-400">
                {meta.total.toLocaleString()} results · {meta.credits_charged} credits used · {meta.remaining_balance} remaining
              </div>
            )}
            {error && <div className="text-xs text-red-400">{error}</div>}
          </div>
        </div>

        {/* Results Table */}
        <div className="mt-6 bg-card-dark border border-gray-700 rounded-xl overflow-hidden">
          {results.length > 0 && (
            <div className="px-4 py-2 border-b border-gray-700 flex items-center justify-between">
              <span className="text-xs text-gray-400">{sorted.length} companies · page {page} · click a row to expand details</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePage(page - 1)}
                  disabled={page <= 1 || loading}
                  className="px-3 py-1 rounded text-xs bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-30"
                >
                  ← Prev
                </button>
                <button
                  onClick={() => handlePage(page + 1)}
                  disabled={sorted.length < pageSize || loading}
                  className="px-3 py-1 rounded text-xs bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-30"
                >
                  Next →
                </button>
                <button
                  onClick={handleExport}
                  className="px-3 py-1 rounded text-xs bg-red-700 hover:bg-red-600 text-white"
                >
                  Export CSV
                </button>
              </div>
            </div>
          )}
          <div className="max-h-[calc(100vh-320px)] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-gray-700">
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-400 uppercase bg-card-dark w-8"></th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-400 uppercase bg-card-dark">Company</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-400 uppercase bg-card-dark">Industry</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-400 uppercase bg-card-dark">Description</th>
                  <SortTh col="match" label="Match" />
                  <SortTh col="size" label="Employees" />
                  <SortTh col="traffic" label="Traffic" />
                  <SortTh col="revenue_annual" label="Revenue" />
                  
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-400 uppercase bg-card-dark">Hiring</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-400 uppercase bg-card-dark">Geo</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-400 uppercase bg-card-dark">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-6 py-12 text-center text-gray-500">
                      {loading ? 'Searching…' : 'No results yet. Enter a definition and criteria above.'}
                    </td>
                  </tr>
                ) : (
                  sorted.map((c, idx) => (
                    <>
                      <tr
                        key={`${c.domain}-${idx}`}
                        onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                        className={`border-b border-gray-800 hover:bg-gray-900/60 cursor-pointer transition-colors ${expandedIdx === idx ? 'bg-gray-900/80' : ''}`}
                      >
                        <td className="px-3 py-3 text-gray-500 text-xs">{expandedIdx === idx ? '▼' : '▶'}</td>
                        <td className="px-3 py-3">
                          <div className="text-white font-semibold text-sm">{c.name || '-'}</div>
                          <a
                            href={c.url || `https://${c.domain}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs text-blue-400 hover:underline"
                          >
                            {c.domain || '-'}
                          </a>
                        </td>
                        <td className="px-3 py-3 text-gray-400 text-xs max-w-[140px] truncate">{c.industry || '-'}</td>
                        <td className="px-3 py-3 text-gray-300 text-xs max-w-[300px]" onClick={(e) => e.stopPropagation()}>
                          <DescriptionCell text={c.description || '-'} />
                        </td>
                        <td className="px-3 py-3 min-w-[120px]">
                          {c.match != null ? (
                            <span className={
                              c.match === 3 ? 'text-xs font-bold text-emerald-400' :
                              c.match === 2 ? 'text-xs font-bold text-yellow-400' :
                              'text-xs text-gray-500'
                            }>
                              {c.match === 3 ? 'Strong' : c.match === 2 ? 'Weak' : 'None'}
                            </span>
                          ) : <span className="text-gray-500 text-xs">-</span>}
                        </td>
                        <td className="px-3 py-3 text-center text-gray-200 text-sm">{c.size ?? '-'}</td>
                        <td className="px-3 py-3 text-center text-gray-200 text-sm">
                          <div>{fmt(c.traffic)}</div>
                          {c.traffic_growth != null && (
                            <div className={`text-xs ${c.traffic_growth >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {c.traffic_growth >= 0 ? '+' : ''}{(c.traffic_growth * 100).toFixed(0)}%
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center text-gray-200 text-sm">{fmt(c.revenue_annual, '$')}</td>
                        <td className="px-3 py-3 text-center text-xs">
                          {c.hiring == null ? (
                            <span className="text-gray-600">-</span>
                          ) : c.hiring ? (
                            <span className="text-emerald-400 font-semibold">Yes</span>
                          ) : (
                            <span className="text-gray-500">No</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center text-gray-300 text-xs">
                          {[c.geo_city, c.geo].filter(Boolean).join(', ') || '-'}
                        </td>
                        <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleViewContacts(c)}
                            className="px-2 py-1 rounded text-white text-xs bg-gray-700 hover:bg-gray-600"
                          >
                            Contacts
                          </button>
                        </td>
                      </tr>
                      {expandedIdx === idx && (
                        <tr key={`${c.domain}-${idx}-expanded`} className="bg-gray-950/50 border-b border-gray-700">
                          <td colSpan={11} className="p-0">
                            <ExpandedRow c={c} maxMatch={maxMatch} />
                          </td>
                        </tr>
                      )}
                    </>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
